import { PAYMENT_PLAN_INPUT_MODE } from '#/api/srm/serializePaymentPlan';

/**
 * 採購異動表單內的付款計畫區塊（兩種採購單共用）。
 * 表單形狀：`paymentPlan = { enabled, inputMode, changeReason, phases: [{ phaseId, amount, percentage, dueDate }] }`。
 * `enabled=false` 時不送 `payment_plan`（總額不變則保留原有效計畫）。
 */

export const PAYMENT_PLAN_INPUT_MODE_OPTIONS = [
  { value: PAYMENT_PLAN_INPUT_MODE.AMOUNT, label: '金額' },
  { value: PAYMENT_PLAN_INPUT_MODE.PERCENTAGE, label: '百分比' },
];

export const PAYMENT_DATA_STATUS = {
  KNOWN: 'KNOWN',
  HISTORICAL_UNKNOWN: 'HISTORICAL_UNKNOWN',
};

const AMOUNT_EPSILON = 0.000001;

function toNumber(value) {
  if (value === '' || value == null) {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function roundTo(value, scale) {
  const factor = 10 ** scale;
  return Math.round(value * factor) / factor;
}

export function emptyPaymentPlanFormValues() {
  return {
    enabled: false,
    inputMode: PAYMENT_PLAN_INPUT_MODE.AMOUNT,
    changeReason: '',
    phases: [],
  };
}

export function createEmptyPaymentPlanPhase() {
  return { phaseId: '', amount: '', percentage: '', dueDate: '' };
}

/**
 * 目前有效計畫（GET payment-plan 的 data）→ 表單期別；每期以 `phases[].id` 承接。
 */
export function mapCurrentPlanToFormPhases(plan) {
  return (plan?.phases ?? []).map((phase) => ({
    phaseId: phase?.id ?? phase?.phaseId ?? '',
    amount: phase?.amount ?? '',
    percentage: phase?.percentage ?? '',
    dueDate: phase?.dueDate ?? '',
  }));
}

/**
 * 異動草稿已存的 `paymentPlan` → 表單值；沒有則依目前有效計畫預填但不啟用。
 */
export function mapPaymentPlanToFormValues(savedPlan, currentPlan) {
  if (savedPlan?.phases?.length) {
    return {
      enabled: true,
      inputMode: savedPlan.inputMode || PAYMENT_PLAN_INPUT_MODE.AMOUNT,
      changeReason: savedPlan.changeReason ?? '',
      phases: savedPlan.phases.map((phase) => ({
        phaseId: phase?.phaseId ?? '',
        amount: phase?.amount ?? '',
        percentage: phase?.percentage ?? '',
        dueDate: phase?.dueDate ?? '',
      })),
    };
  }
  return {
    ...emptyPaymentPlanFormValues(),
    phases: mapCurrentPlanToFormPhases(currentPlan),
  };
}

/** 表單值 → 送出用（未啟用回 undefined，序列化時整個省略）。 */
export function pickPaymentPlanForWrite(formPlan) {
  if (!formPlan?.enabled) {
    return undefined;
  }
  return {
    inputMode: formPlan.inputMode,
    changeReason: formPlan.changeReason,
    phases: formPlan.phases ?? [],
  };
}

/** 新舊應付總額是否不同（前端預覽值，僅作提示；後端以保存後總額為準）。 */
export function hasTotalAmountChanged(originalTotal, nextTotal, scale = 4) {
  const before = toNumber(originalTotal);
  const after = toNumber(nextTotal);
  if (before == null || after == null) {
    return false;
  }
  return Math.abs(roundTo(before, scale) - roundTo(after, scale)) > AMOUNT_EPSILON;
}

/**
 * 期別合計摘要：金額模式比對新總額，百分比模式比對 100。
 * @returns {{ sum: number, target: number, matched: boolean, mode: string }}
 */
export function summarizePaymentPlanPhases(formPlan, totalAmount) {
  const mode = formPlan?.inputMode === PAYMENT_PLAN_INPUT_MODE.PERCENTAGE
    ? PAYMENT_PLAN_INPUT_MODE.PERCENTAGE
    : PAYMENT_PLAN_INPUT_MODE.AMOUNT;
  const field = mode === PAYMENT_PLAN_INPUT_MODE.PERCENTAGE ? 'percentage' : 'amount';
  const sum = (formPlan?.phases ?? []).reduce(
    (acc, phase) => acc + (toNumber(phase?.[field]) ?? 0),
    0,
  );
  const target = mode === PAYMENT_PLAN_INPUT_MODE.PERCENTAGE
    ? 100
    : (toNumber(totalAmount) ?? 0);
  const scale = mode === PAYMENT_PLAN_INPUT_MODE.PERCENTAGE ? 6 : 4;
  return {
    mode,
    sum: roundTo(sum, scale),
    target,
    matched: Math.abs(roundTo(sum, scale) - roundTo(target, scale)) <= AMOUNT_EPSILON,
  };
}

/**
 * 提交前的前端把關（只擋明確缺漏，金額精度由後端最終驗證）。
 * @returns {string|null} 錯誤訊息
 */
export function validatePaymentPlanBeforeSubmit(formPlan, { originalTotal, nextTotal }) {
  const totalChanged = hasTotalAmountChanged(originalTotal, nextTotal);
  if (!formPlan?.enabled) {
    return totalChanged ? '應付總額已變更，請一併調整付款計畫' : null;
  }
  const phases = formPlan.phases ?? [];
  if (phases.length === 0) {
    return '付款計畫至少需要一期';
  }
  const field = formPlan.inputMode === PAYMENT_PLAN_INPUT_MODE.PERCENTAGE ? 'percentage' : 'amount';
  if (phases.some((phase) => toNumber(phase?.[field]) == null || toNumber(phase?.[field]) < 0)) {
    return field === 'percentage' ? '每期都需填寫百分比' : '每期都需填寫金額';
  }
  const seen = new Set();
  for (const phase of phases) {
    if (phase?.phaseId) {
      if (seen.has(phase.phaseId)) {
        return '同一個原期別只能承接一次';
      }
      seen.add(phase.phaseId);
    }
  }
  return null;
}
